import type { Item } from '@revelio/iso-utility';

import { AdditionalReportSelectionCategories } from './utils';

export const LIMITED_COMPANY_SELECTION_LISTS = [
  AdditionalReportSelectionCategories.COMPANY_REPVUE,
  AdditionalReportSelectionCategories.COMPANY_ORGCHART,
] as const;

export type LimitedCompanySelectionListId =
  (typeof LIMITED_COMPANY_SELECTION_LISTS)[number];

const companyFilterRcidsCache = new Map<string, Promise<Set<number> | null>>();

const parseCompanyFilterRcids = (jsonData: unknown): Set<number> | null => {
  if (!Array.isArray(jsonData)) {
    return null;
  }

  const rcids = jsonData
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value));

  return new Set(rcids);
};

export const isLimitedCompanySelectionList = (
  listId: string
): listId is LimitedCompanySelectionListId =>
  LIMITED_COMPANY_SELECTION_LISTS.includes(
    listId as LimitedCompanySelectionListId
  );

export const loadCompanyFilterRcids = (
  listId: string
): Promise<Set<number> | null> => {
  const cached = companyFilterRcidsCache.get(listId);
  if (cached) {
    return cached;
  }

  const loadPromise = (async (): Promise<Set<number> | null> => {
    try {
      const response = await fetch(
        `/assets/reports/company-filters/${listId}.json`
      );
      if (!response.ok) {
        return null;
      }
      const jsonData: unknown = await response.json();
      return parseCompanyFilterRcids(jsonData);
    } catch {
      return null;
    }
  })();

  companyFilterRcidsCache.set(listId, loadPromise);
  return loadPromise;
};

export const toCompanyFilterFn = (
  rcids: Set<number>
): ((item: Item) => boolean) => {
  return (item: Item) => {
    const rcid = Number(item.rcid ?? item.id);
    return !Number.isNaN(rcid) && rcids.has(rcid);
  };
};

export const prefetchLimitedCompanyFilterRcids = async (
  listIds: readonly string[]
): Promise<Map<string, Set<number> | null>> => {
  const uniqueLimitedListIds = [
    ...new Set(listIds.filter(isLimitedCompanySelectionList)),
  ];

  const entries = await Promise.all(
    uniqueLimitedListIds.map(
      async (listId) => [listId, await loadCompanyFilterRcids(listId)] as const
    )
  );

  return new Map(entries);
};
